import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { MenuItem, Button, Grid, TextField } from "@mui/material";
import moment from "moment";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useAuth } from "hooks/useAuth";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useGetContractorUserDataDetailQuery } from "store/services/contractorUserApi";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

const INITIAL_STATE = {
  account: "",
  entity: "",
  registration_number: "",
  vehicleType: "",
  capacity: "",
  unit: "Ton",
  maintenance_due_date: "",
  ownership_type: "",
  status: true,
  current_location: { lat: "", lon: "" },
  fuel_level: "",
  contractorProfile: null,
};

const VehicleForm = ({
  isBusy = false,
  vehicleDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const {
    viewEntity,
    isAccountAdmin,
    isSystemAdmin,
    isContractor,
    loggedInUser,
  } = useAuth();

  // RTK Query
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const { data: contractorData, isLoading: loadingContractorData } =
    useGetContractorUserDataDetailQuery(loggedInUser?.typeRef, {
      skip: !isContractor || !loggedInUser?.typeRef,
    });

   const { data: vehicleTypes = [], isLoading: loadingVehicle } =
      useListListQuery({
        type: ENTITY_LIST_CONSTANTS.VEHICLE_TYPES,
        status: "Active",
      });
    const _mappedVehicleType = React.useMemo(
      () =>
        Array.isArray(vehicleTypes)
          ? vehicleTypes.map((item) => ({
              label: item?.name,
              value: item?._id,
            }))
          : [],
      [vehicleTypes]
    );
  
    const { data: ownershipTypes = [] } =
       useListListQuery({
        type: ENTITY_LIST_CONSTANTS.VEHICLE_OWNERSHIP_TYPES,
        status: "Active",
      });
    const _mappedOwnershipType = React.useMemo(
      () =>
        Array.isArray(ownershipTypes)
          ? ownershipTypes.map((item) => ({
              label: item?.name,
              value: item?._id,
            }))
          : [],
      [ownershipTypes]
    );
  

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
    },
  });
  const _watchAccount = useWatch({ control, name: "account" });

  // Handle form submission
  const handleOnVehicleSubmit = (data) => {
    const _vehiclePayLoad = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      vehicleType: data?.vehicleType,
      insurance_id: data?.insurance_id?._id,
      contractor_id: isContractor ? data?.contractorProfile?._id : undefined,
      ownership_type: data?.ownership_type,
    };
    onSubmit(_vehiclePayLoad);
  };

  // Populate form for editing
  React.useEffect(() => {
    if (!vehicleDetail || loadingVehicle) return;

    reset({
      ...INITIAL_STATE,
      account: vehicleDetail?.account ?? viewEntity?.entity?.account ?? "",
      entity: vehicleDetail?.entity ?? viewEntity?.entity ?? "",
      registration_number: vehicleDetail?.registration_number ?? "",
      vehicleType: vehicleDetail?.vehicleType?._id ?? vehicleDetail?.vehicleType ?? "",
      capacity: vehicleDetail?.capacity ?? "",
      unit: vehicleDetail?.unit ?? "Ton",
      maintenance_due_date: vehicleDetail?.maintenance_due_date
        ? moment(vehicleDetail.maintenance_due_date).format("YYYY-MM-DD")
        : "",
      ownership_type: vehicleDetail?.ownership_type?._id ?? vehicleDetail?.ownership_type ?? "",
      status: vehicleDetail?.status ?? true,
      current_location: vehicleDetail?.current_location ?? { lat: "", lon: "" },
      fuel_level: vehicleDetail?.fuel_level ?? "",
      contractorProfile: vehicleDetail?.contractor_id
        ? { _id: vehicleDetail.contractor_id }
        : null,
    });
  }, [vehicleDetail, reset, viewEntity?.entity, loadingAccounts, loadingVehicle]);

  // Fetch entities for admins
  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id || viewEntity?.entity?.account,
      });
    }
  }, [
    isAccountAdmin,
    isSystemAdmin,
    _watchAccount?._id,
    fetchEntities,
    viewEntity?.entity?.account,
  ]);

  return (
    <AppForm loading={isBusy || loadingVehicle}>
      <AppForm.Content>
        <Grid container spacing={2}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Account"
                    required
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("insurance_id", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {isContractor && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="contractorProfile"
                control={control}
                rules={{ required: "Please select a contractor profile." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Contractor Profile"
                    value={value || ""}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={contractorData?.contractorProfiles || []}
                    loading={loadingContractorData}
                    loadingText="Loading Contractor Profiles..."
                    helperText={errors?.contractorProfile?.message}
                    error={!!errors?.contractorProfile}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Controller
              name="registration_number"
              control={control}
              rules={{ required: "Please provide a Registration Number." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Registration Number"
                  error={!!errors.registration_number}
                  helperText={errors?.registration_number?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="vehicleType"
              control={control}
              rules={{
                required: "Add Vehicle Type",
                validate: (value) =>
                  _mappedVehicleType.some((opt) => opt.value === value) ||
                  "Invalid vehicle type",
              }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Vehicle Type"
                  options={_mappedVehicleType}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.vehicleType}
                  helperText={errors?.vehicleType?.message}
                  loading={loadingVehicle}
                  loadingText="Loading Vehicle Types..."
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="capacity"
              control={control}
              rules={{
                required: "Please provide a Capacity value.",
                min: { value: 1, message: "Capacity must be at least 1." },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Capacity"
                  type="number"
                  error={!!errors.capacity}
                  helperText={errors?.capacity?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="unit"
              control={control}
              defaultValue="Ton"
              rules={{ required: "Please select a unit." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  select
                  fullWidth
                  size="small"
                  label="Unit"
                  error={!!errors.unit}
                  helperText={errors?.unit?.message}
                >
                  <MenuItem value="Ton">Ton</MenuItem>
                  <MenuItem value="Unit">Unit</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="maintenance_due_date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Maintenance Due Date (Optional)"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                  }
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="ownership_type"
              control={control}
              rules={{
                required: "Add Ownership Type",
                validate: (value) =>
                  _mappedOwnershipType.some((opt) => opt.value === value) ||
                  "Invalid ownership type",
              }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Ownership Type"
                  options={_mappedOwnershipType}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.ownership_type}
                  helperText={errors?.ownership_type?.message}
                  loading={_mappedOwnershipType}
                  loadingText="Loading Ownership Types..."
                />
              )}
            />
          </Grid>

          {vehicleDetail && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnVehicleSubmit)}
        >
          {vehicleDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default VehicleForm;